import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded'
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SettingsIcon from '@mui/icons-material/Settings'
import TodayIcon from '@mui/icons-material/Today'
import { Box, Button, Dialog, Drawer, IconButton, LinearProgress, Popover, Select, Typography, useMediaQuery, useTheme } from '@mui/material'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ModernChamberVideoInterface from './components/ModernChamberVideoInterface'
import dayjs from 'dayjs'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import aumhumRazorpayQR from 'src/assets/aumhumRazorpayQR.jpg'
import useAumhumScreenSignals from 'src/chamberScreen/hooks/useAumhumScreenSignals'
import PopupQRInnerComponent from 'src/chamberScreen/layout/components/PopupQRInnerComponent'
// Removed getNameOfVirtualChamberRoom import - using displayName from API 1 instead
import { CADeviceUseFixingComponent } from 'src/clinic/components/ClinicHomePage/components/CADeviceUseFixingComponent'
import useAumhumScreen from 'src/clinic/components/ClinicHomePage/hooks/useAumhumScreen'
import { buildChamberScreenRtmUsername } from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/utils/rtmUsernameBuilder'
import { getCPDataWithoutParams, getCPDataWithParams, postCPDataWithParams, postCPDataWithoutParams } from 'src/clinic/services'
import { checkAvailableDevices } from 'src/consumer/components/LiveSessionPage'
import AskCameraMicPermissionsDialog from 'src/consumer/components/PublicLiveSessionPage/components/AskCameraMicPermissionsDialog'
import JoinSessionRelatedButtons from 'src/consumer/components/PublicLiveSessionPage/components/JoinSessionRelatedButtons'
import {
  APP_CONSTANTS,
  APP_UPDATE_ACTIONS,
  CHAMBER_SCREEN_ACTIONS,
  CHAMBER_SCREEN_STATES,
  DISPATCH_EVENT_KEYS,
  SCREEN_DEVICE_CONFIG_INSTALLATION_TYPES,
  SIGNALING_COMMANDS,
} from 'src/consumer/constants'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import {
  getDataWithoutParams,
  putDataWithParams,
  postDataWithoutParams,
} from 'src/consumer/services/featureServices'
import {
  getAumhumScreenId,
  getAccessTokenExpiryReadable,
  getTimeUntilExpiry,
  getAppInstallationId,
  setAccessTokenExpiry,
  setSelectedCameraInLocalStorage,
  setSelectedMicrophoneInLocalStorage,
  setSelectedSpeakerInLocalStorage,
} from 'src/consumer/services/localstorageServices'
import { logoutHandler } from 'src/consumer/services/loginLogoutHandlers'
import { getData } from 'src/consumer/services/profileService'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { DocSideContext } from 'src/context/DocSideContextProvider'
import {
  getImageLink,
  isCommonResponseSuccessful,
  isCommonResponseSuccessfulV2,
  sleep,
} from 'src/mentor/helpers/utilityFunctions'
import { ensureOrbitronFontLoaded } from 'src/utils/loadOnDemandFonts'
import {
  AGORA_RTC_LAYOUTS,
  FLOW_TYPES,
  addWindowSignalingEventListener,
  createChamberScreenLocalParticipantIdentity,
  readLocalParticipantIdentity,
} from 'src/live'
import styled, { createGlobalStyle } from 'styled-components'
import packageJson from '../../../package.json'
import DigitalClockComponent from './DigitalClickComponent'
import { PinEntryVarOne } from './PinEntryVarOne'
import PaymentLinkQrWithPatientDetails from './components/PaymentLinkQrWithPatientDetails'
import TokenExpiryTimeForChamberRoom from 'src/chamberScreen/layout/components/TokenExpiryTimeForChamberRoom'

// Global styles for calm, slow animations
const GlobalAnimations = createGlobalStyle`
  @keyframes float {
    0%, 100% {
      transform: translateY(0px) translateX(0px);
      opacity: 0.6;
    }
    50% {
      transform: translateY(-20px) translateX(15px);
      opacity: 0.8;
    }
  }
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
    }
    50% {
      opacity: 0.8;
      box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
    }
  }
`

const KIOSK_DEBUG_LOG_TYPE = 'manual-kiosk-screen-debug-report'

function buildHardReloadUrl() {
  const nextUrl = new URL(window.location.href)
  nextUrl.searchParams.set('hardReloadTs', String(Date.now()))
  return nextUrl.toString()
}

function hardReloadApp() {
  window.location.replace(buildHardReloadUrl())
}

export default function KioskLayout() {
  // const fixedChamberRoomId = localStorage.getItem('selectedChamberRoomIdForScreen')
  // Get config - use kiosk-config with fallback to ROLE_CA_CONFIG (consistent with other components)
  // Use useMemo to prevent recreation on every render
  const roleCaConfigData = useMemo(() => {
    const kioskConfigStr = localStorage.getItem('kiosk-config')
    const roleCaConfigStr = localStorage.getItem('ROLE_CA_CONFIG')
    return kioskConfigStr ? JSON.parse(kioskConfigStr) : (roleCaConfigStr ? JSON.parse(roleCaConfigStr) : {})
  }, [])
  const fixedChamberRoomId = roleCaConfigData?.CURRENT_VIRTUAL_ROOM_ID
  const screenDeviceConfigId = roleCaConfigData?.SCREEN_DEVICE_CONFIG_ID
  // const clinicIdOfFixedChamberRoomId = localStorage.getItem(
  //   'clinicIdOfSelectedChamberRoomIdForScreen',
  // )
  // const { state } = useLocation()
  // const clinicLocationData = state?.clinicLocationData // Use optional chaining
  // const [askResetDialogOpen, setAskResetDialogOpen] = useState()

  const { registerScreen, unregisterScreen } = useAumhumScreen()

  useEffect(() => {
    ensureOrbitronFontLoaded()
  }, [])

  const [screenDeviceConfigs, setScreenDeviceConfigs] = useState([])
  const [virtualChamberRooms, setVirtualChamberRooms] = useState([]) // API 1: virtual chamber rooms with all data
  const [brands, setBrands] = useState([]) // API 2: brands with locations
  const [wantedClinicLocation, setWantedClinicLocation] = useState()
  const [wantedChamberRoomId, setWantedChamberRoomId] = useState()
  const [thisChamberRoomData, setThisChamberRoomData] = useState()
  const [thisClinicLocationData, setThisClinicLocationData] = useState()
  const [thisChamberName, setThisChamberName] = useState('---')
  const [thisLocationName, setThisLocationName] = useState('---')
  const [thisBrandName, setThisBrandName] = useState('---')
  const [getNextActionLoading, setGetNextActionLoading] = useState(false)
  const [serverGivenNextAction, setServerGivenNextAction] = useState()
  const [allowedRetryAttempts, setAllowedRetryAttempts] = useState(5) // how many times this person can Retry connecting to clinic manually
  const [screenOnSince, setScreenOnSince] = useState(dayjs())

  const [popupTimeLeftOnScreen, setPopupTimeLeftOnScreen] = useState(0)
  const [maxTimeForPopup, setMaxTimeForPopup] = useState(30)
  const [imageSourceForPopup, setImageSourceForPopup] = useState(null)
  const [additionalTextAboveImage, setAdditionalTextAboveImage] = useState('')

  const [chamberScreenStateNow, setChamberScreenStateNow] = useState(CHAMBER_SCREEN_STATES.IDLING)
  const [agoraRtcProps, setAgoraRtcProps] = useState({})
  const [agoraRtmProps, setAgoraRtmProps] = useState({})
  const [joinedChannelName, setJoinedChannelName] = useState('')
  const [brandAndClinicsWholeData, setBrandAndClinicsWholeData] = useState([])

  const [paymentLinkWithQRCodeData, setPaymentLinkWithQRCodeData] = useState()

  const [poppType, setPopupType] = useState('')
  const [hasRegisteredScreenCounter, setHasRegisteredScreenCounter] = useState(0)
  const hasProcessedChamberRoomData = useRef(false)
  const lastProcessedChamberRoomId = useRef(null)
  const registrationIntervalRef = useRef(null)

  const [microphoneError, setMicrophoneError] = useState(false)
  const [cameraError, setCameraError] = useState(false)
  const [availableCameras, setAvailableCameras] = useState([])
  const [availableMicrophones, setAvailableMicrophones] = useState([])
  const [availableSpeakers, setAvailableSpeakers] = useState([])
  const [selectedCamera, setSelectedCamera] = useState(null)
  const [selectedMicrophone, setSelectedMicrophone] = useState(null)
  const [selectedSpeaker, setSelectedSpeaker] = useState(null)
  const [giveMicroPhonePermissionDialogOpen, setGiveMicrophonePermissionDialogOpen] =
    useState(false)
  const [giveCamperPermissionDialogOpen, setGiveCameraPermissionDialogOpen] = useState(false)
  const [giveBothPermissionsDialogOpen, setGiveBothPermissionsDialogOpen] = useState(false)
  const { setFlowId, setShowDocSide } = useContext(DocSideContext)

  const registerAumhumScreenUsingRoomDataAndClinicData = useCallback((
    chamberRoomData,
    clinicData,
    forceUpdate = false,
  ) => {
    if (!chamberRoomData || !clinicData) {
      return
    }
    const clinicId = chamberRoomData?.clinicLocationId
    const clinicBrandId = clinicData?.clinicBrandId
    const additionalData = {
      screenDeviceConfigId: screenDeviceConfigId,
      chamberRoomId: fixedChamberRoomId,
      clinicLocationId: clinicId,
      clinicBrandId: clinicData?.clinicBrandId,
    }
    registerScreen('remote-doctor', additionalData, forceUpdate)
  }, [screenDeviceConfigId, fixedChamberRoomId, registerScreen])

  const _ = useAumhumScreenSignals(
    registerAumhumScreenUsingRoomDataAndClinicData,
    setSelectedMicrophone,
    setSelectedSpeaker,
    thisChamberRoomData,
    thisClinicLocationData,
    () => generateAndStoreNewToken(true), // Pass token generation function for signals
  )

  // API 1: Get screen device configs and virtual chamber rooms
  async function getAndSetScreenDeviceConfigs() {
    try {
      const resp = await getCPDataWithoutParams('/api/secure/kiosk/screen-device-configs')
      if (resp?.status === 200 && resp?.data?.code === 2000) {
        const screenDeviceConfigsData = resp?.data?.data?.screenDeviceConfigs || []
        const virtualChamberRoomsData = resp?.data?.data?.virtualChamberRooms || []
        setScreenDeviceConfigs(screenDeviceConfigsData)
        setVirtualChamberRooms(virtualChamberRoomsData)
      } else {
        console.error('Error fetching screen device configs:', resp?.data?.message)
      }
    } catch (error) {
      console.error('Error fetching screen device configs:', error)
    }
  }

  // API 2: Get clinics (brands and locations) - includes WhatsApp QR code
  async function getAndSetClinics() {
    try {
      const resp = await getCPDataWithoutParams('/api/secure/kiosk/clinics')
      if (resp?.status === 200 && resp?.data?.code === 2000) {
        const brandsData = resp?.data?.data?.brands || []
        setBrands(brandsData)
      } else {
        console.error('Error fetching clinics:', resp?.data?.message)
      }
    } catch (error) {
      console.error('Error fetching clinics:', error)
    }
  }

  // Get brand data including WhatsApp QR code from API 2 response
  const getBrandData = useCallback((brandId) => {
    const brand = brands.find((b) => b?.clinicBrandId === brandId)
    if (brand) {
      setBrandAndClinicsWholeData({
        clinicBrand: {
          id: brand.clinicBrandId,
          name: brand.clinicBrandName,
          webAddressForWhatsappInitialMessage: {
            qrCodeMediaId: brand.whatsappSupportQrCodeMediaId,
          },
        },
      })
    }
  }, [brands])

  const getAndHandleNextAction = useCallback(async () => {
    if (fixedChamberRoomId) {
      console.log('[KIOSK PORTAL] getAndHandleNextAction called', {
        chamberRoomId: fixedChamberRoomId,
        screenState: chamberScreenStateNow,
        joinedChannelName: agoraRtcProps?.channel,
        screenId: getAumhumScreenId(),
        timestamp: new Date().toISOString()
      })
      
      setGetNextActionLoading(true)
      const response = await getCPDataWithParams(`/api/secure/kiosk/chamber-screen/get-next-action`, {
        chamberRoomId: fixedChamberRoomId,
        screenState: chamberScreenStateNow,
        joinedChannelName: agoraRtcProps?.channel,
        screenId: getAumhumScreenId(),
      })
      setGetNextActionLoading(false)
      
      console.log('[KIOSK PORTAL] get-next-action response:', {
        status: response?.status,
        code: response?.data?.code,
        action: response?.data?.data?.chamberScreenAction?.action,
        fullResponse: response?.data?.data,
        timestamp: new Date().toISOString()
      })
      
      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        handleIncomingNextActionFromServer(response?.data?.data)
        setServerGivenNextAction(response?.data?.data)
      } else {
        console.error('[KIOSK PORTAL] Error getting next action:', {
          status: response?.status,
          code: response?.data?.code,
          message: response?.data?.message
        })
        toast.error('Error getting next action')
      }
    } else {
      console.warn('[KIOSK PORTAL] getAndHandleNextAction called but fixedChamberRoomId is not set')
    }
  }, [chamberScreenStateNow, fixedChamberRoomId, agoraRtcProps])

  async function handleIncomingNextActionFromServer(nextActionData) {
    const data = nextActionData?.chamberScreenAction
    console.log('[KIOSK PORTAL] handleIncomingNextActionFromServer called', {
      action: data?.action,
      chamberRoomName: data?.chamberRoomName,
      joinChannelName: data?.joinChannelName,
      fullData: data,
      timestamp: new Date().toISOString()
    })
    
    setThisChamberName(data?.chamberRoomName)
    switch (data?.action) {
      case CHAMBER_SCREEN_ACTIONS.ACTION_JOIN:
        console.log('[KIOSK PORTAL] ACTION_JOIN received - joining Agora channel:', data?.joinChannelName)
        toast.info('Server commanded to join a session...')
        setTimeout(() => joinAgoraChannelAndStartVideo(data), 0)
        //inform server that you have joined the channel. Update with your screenId and chamberRoomId
        break
      case CHAMBER_SCREEN_ACTIONS.ACTION_CONTINUE_SESSION:
        // toast.info('Server commanded to continue session...')
        setTimeout(() => continueSession(data), 5000)
        break
      case CHAMBER_SCREEN_ACTIONS.ACTION_LEAVE:
        toast.info('Server commanded to leave the current session...')
        setTimeout(() => leaveThisSession(), 5000)
        break
      case CHAMBER_SCREEN_ACTIONS.ACTION_CONTINUE_IDLING:
        // toast.info('Server commanded to continue idling...')
        setTimeout(() => continueIdling(data), 5000)
        break
      default:
        console.log('Unknown action:', data?.action)
        break
    }
  }

  async function joinAgoraChannelAndStartVideo(data) {
    informChamberRoomChannelJoin(data?.joinChannelName)
    setJoinedChannelName(data?.joinChannelName)
    setChamberScreenStateNow(CHAMBER_SCREEN_STATES.SESSION_RUNNING)
    const resolvedVideoQuality =
      data?.sessionRoutingDecision?.videoQuality || data?.videoQuality || null
    setAgoraRtcProps({
      appId: import.meta.env.VITE_AGORA_APP_ID,
      channel: data?.joinChannelName,
      uid: parseInt(data?.joinChannelUid),
      token: data?.joinChannelRtcToken,
      layout: AGORA_RTC_LAYOUTS.pin,
      role: 'host',
      enableDualStream: resolvedVideoQuality?.enableDualStream === true,
      enableAudio: true,
      enableVideo: true,
      videoQuality: resolvedVideoQuality,
    })
    const rtmUsername = buildChamberScreenRtmUsername(
      data?.displayName || 'Chamber Screen',
      getAumhumScreenId() || screenDeviceConfigId || fixedChamberRoomId,
    )
    
    // Parse to show the object being sent
    let parsedUsername = null
    try {
      parsedUsername = JSON.parse(rtmUsername)
    } catch (error) {
      console.error('Error parsing RTM username before sending:', error)
      parsedUsername = { error: 'Failed to parse' }
    }
    
    console.log('=== SETTING AGORA RTM PROPS (SENDER - Chamber Screen) ===', {
      rawUsername: rtmUsername,
      parsedObject: parsedUsername,
      objectStructure: {
        role: parsedUsername?.role || 'MISSING',
        name: parsedUsername?.name || 'MISSING',
        profilePicId: parsedUsername?.profilePicId || 'MISSING (null)',
      },
      screenDisplayName: data?.displayName || 'Chamber Screen',
    })
    
    setAgoraRtmProps({
      username: rtmUsername,
      displayUsername: true,
      token: data?.joinChannelRtmToken,
    })
  }

  function continueIdling(data) {
    setChamberScreenStateNow(CHAMBER_SCREEN_STATES.IDLING)
  }

  function continueSession(data) {
    setChamberScreenStateNow(CHAMBER_SCREEN_STATES.SESSION_RUNNING)
  }

  async function informChamberRoomChannelJoin(channelName) {
    const responseOne = await postCPDataWithParams(
      `/api/secure/kiosk/chamber-screen/log/join`,
      {
        channelName: channelName,
        screenId: getAumhumScreenId(),
      },
      {},
    )
    if (responseOne?.status === 200 && isCommonResponseSuccessful(responseOne?.data?.code)) {
      console.log('informed join channel to server')
    } else {
      console.log('could not inform join channel to server')
    }
  }

  async function informChamberRoomChannelLeave(channelName) {
    const responseOne = await postCPDataWithParams(
      `/api/secure/kiosk/chamber-screen/log/leave`,
      {
        channelName: channelName,
        screenId: getAumhumScreenId(),
      },
      {},
    )
    if (responseOne?.status === 200 && isCommonResponseSuccessful(responseOne?.data?.code)) {
      console.log('informed leave channel to server')
    } else {
      console.log('could not inform leave channel to server')
    }
  }

  async function leaveThisSession() {
    informChamberRoomChannelLeave(joinedChannelName)
    setChamberScreenStateNow(CHAMBER_SCREEN_STATES.IDLING)
    setAgoraRtcProps({})
    setAgoraRtmProps({})
  }

  async function generateAndStoreNewToken(forceReRegistration = false) {
    try {
      const applicationId = getAppInstallationId()
      const screenId = getAumhumScreenId()

      if (!applicationId || !screenId) {
        console.error('Missing applicationId or screenId for token generation')
        return { success: false, error: 'Missing applicationId or screenId' }
      }

      console.log('Generating new token...', { forceReRegistration })

      const response = await postDataWithoutParams('/api/secure/kiosk/screen/token/generate/new', {
        applicationId,
        screenId,
      })

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const newToken = response.data?.data?.token
        const newTokenExpiryMs = response.data?.data?.tokenExpiryMs

        if (newToken && newTokenExpiryMs) {
          localStorage.setItem('token', newToken)
          setAccessTokenExpiry({ accessTokenExpiry: newTokenExpiryMs })

          const logMessage = forceReRegistration ?
            'New token generated successfully, re-registering screen...' :
            'Token refreshed successfully'
          console.log(logMessage)

          // Re-register the aumhum screen with the old screen ID and new token data if forced or no existing token
          if (forceReRegistration && thisChamberRoomData && thisClinicLocationData) {
            registerAumhumScreenUsingRoomDataAndClinicData(thisChamberRoomData, thisClinicLocationData, true)
          }

          return { success: true, token: newToken, tokenExpiryMs: newTokenExpiryMs }
        } else {
          console.error('Invalid token generation response format')
          return { success: false, error: 'Invalid token generation response format' }
        }
      } else {
        console.error('Token generation failed:', response?.status, response?.data)
        return { success: false, error: `Token generation failed: ${response?.status}` }
      }
    } catch (error) {
      console.error('Error during token generation:', error)
      return { success: false, error: error.message }
    }
  }

  async function checkAndRefreshTokenIfNeeded() {
    try {
      const timeUntilExpiry = getTimeUntilExpiry()

      if (!timeUntilExpiry) {
        console.log('Token expiry not available or already expired, generating new token...')
        const result = await generateAndStoreNewToken(true) // Force re-registration when no token
        if (result.success) {
          window.location.reload()
        }
        return
      }

      const threeDaysInMs = 3 * 24 * 60 * 60 * 1000 // 3 days in milliseconds

      if (timeUntilExpiry.totalMs <= threeDaysInMs) {
        console.log('Token expiring within 3 days, refreshing token...')
        const result = await generateAndStoreNewToken(false) // Regular refresh, no re-registration needed
        if (result.success) {
          window.location.reload()
        }
      } else {
        console.log(
          `Token valid for ${Math.round(
            timeUntilExpiry.totalMs / (24 * 60 * 60 * 1000),
          )} more days`,
        )
      }
    } catch (error) {
      console.error('Error during token refresh check:', error)
    }
  }

  useEffect(() => {
    // Clear any existing interval when effect runs
    if (registrationIntervalRef.current) {
      clearInterval(registrationIntervalRef.current)
      registrationIntervalRef.current = null
    }

    // Use virtualChamberRooms from API 1 instead of chamber-room/list
    if (Array.isArray(virtualChamberRooms) && Array.isArray(brands)) {
      if (fixedChamberRoomId) {
        // Check if we've already processed this chamber room ID to prevent infinite loops
        if (lastProcessedChamberRoomId.current === fixedChamberRoomId && hasProcessedChamberRoomData.current) {
          return
        }

        // Find the virtual chamber room from API 1 (includes all necessary data)
        const fixedChamberRoom = virtualChamberRooms?.find(
          (item) => item?.virtualChamberRoomId === fixedChamberRoomId,
        )
        if (fixedChamberRoom) {
          const clinicId = fixedChamberRoom?.clinicLocationId
          const foundClinicBrandId = fixedChamberRoom?.clinicBrandId
          
          // Find clinic location from brands data (API 2)
          let foundClinic = null
          for (const brand of brands) {
            if (Array.isArray(brand?.locations)) {
              foundClinic = brand.locations.find((loc) => loc?.id === clinicId)
              if (foundClinic) break
            }
          }
          
          // Get brand data including WhatsApp QR code from API 2
          getBrandData(foundClinicBrandId)
          
          // Only log once per chamber room ID change
          if (lastProcessedChamberRoomId.current !== fixedChamberRoomId) {
            console.log('[KIOSK PORTAL] Found chamber room data:', {
              chamberRoom: fixedChamberRoom,
              clinic: foundClinic,
              brandId: foundClinicBrandId,
            })
          }
          
          setThisBrandName(fixedChamberRoom?.clinicBrandName || '---')
          setThisLocationName(fixedChamberRoom?.clinicLocationName || '---')
          setThisChamberName(fixedChamberRoom?.title || '---')

          // Set chamber room data (using structure from API 1)
          setThisChamberRoomData({
            id: fixedChamberRoom.virtualChamberRoomId,
            clinicLocationId: fixedChamberRoom.clinicLocationId,
            title: fixedChamberRoom.title,
          })
          
          // Set clinic location data
          setThisClinicLocationData(foundClinic || {
            id: clinicId,
            name: fixedChamberRoom.clinicLocationName,
            clinicBrandId: foundClinicBrandId,
            clinicBrandName: fixedChamberRoom.clinicBrandName,
          })

          // Register screen with proper additional data now that we have all the info
          const localScreenId = getAumhumScreenId()
          const additionalData = {
            screenDeviceConfigId: screenDeviceConfigId,
            chamberRoomId: fixedChamberRoomId,
            clinicLocationId: clinicId,
            clinicBrandId: foundClinicBrandId,
          }
          
          // Only log once per chamber room ID change
          if (lastProcessedChamberRoomId.current !== fixedChamberRoomId) {
            console.log('[KIOSK PORTAL] Screen registration data:', { additionalData })
          }
          
          // Register screen only once per session
          // If screen ID already exists, use forceUpdate=true to update existing registration
          if (screenDeviceConfigId && hasRegisteredScreenCounter < 1) {
            const shouldForceUpdate = !!localScreenId // If screen ID exists, force update
            if (shouldForceUpdate) {
              console.log('[KIOSK PORTAL] Screen ID already exists, updating registration with forceUpdate=true')
            } else {
              console.log('[KIOSK PORTAL] Registering new screen')
            }
            
            setHasRegisteredScreenCounter(hasRegisteredScreenCounter + 1) // Prevent multiple registrations
            registerAumhumScreenUsingRoomDataAndClinicData(
              {
                id: fixedChamberRoom.virtualChamberRoomId,
                clinicLocationId: fixedChamberRoom.clinicLocationId,
              },
              foundClinic || {
                id: clinicId,
                name: fixedChamberRoom.clinicLocationName,
                clinicBrandId: foundClinicBrandId,
                clinicBrandName: fixedChamberRoom.clinicBrandName,
              },
              shouldForceUpdate, // Pass forceUpdate flag
            )
          }
          
          // Mark as processed and store the chamber room ID
          hasProcessedChamberRoomData.current = true
          lastProcessedChamberRoomId.current = fixedChamberRoomId
          
          registrationIntervalRef.current = setInterval(() => {
            registerAumhumScreenUsingRoomDataAndClinicData(
              {
                id: fixedChamberRoom.virtualChamberRoomId,
                clinicLocationId: fixedChamberRoom.clinicLocationId,
              },
              foundClinic || {
                id: clinicId,
                name: fixedChamberRoom.clinicLocationName,
                clinicBrandId: foundClinicBrandId,
                clinicBrandName: fixedChamberRoom.clinicBrandName,
              },
              true, // Always use forceUpdate=true for interval updates (screen is already registered)
            )
          }, 21600000) // 6 hours
        }
      } else {
        // Reset flags if fixedChamberRoomId is cleared
        hasProcessedChamberRoomData.current = false
        lastProcessedChamberRoomId.current = null
      }
    }
    return () => {
      if (registrationIntervalRef.current) {
        clearInterval(registrationIntervalRef.current)
        registrationIntervalRef.current = null
      }
    }
  }, [virtualChamberRooms, brands, fixedChamberRoomId, screenDeviceConfigId, hasRegisteredScreenCounter, getBrandData, registerAumhumScreenUsingRoomDataAndClinicData])

  useEffect(() => {
    // Initial check on mount
    const checkDevices = async () => {
      try {
        await checkAvailableDevices(
          setMicrophoneError,
          setCameraError,
          setAvailableCameras,
          setAvailableMicrophones,
          setAvailableSpeakers,
          setSelectedCamera,
          setSelectedMicrophone,
          setSelectedSpeaker,
          setGiveBothPermissionsDialogOpen,
        )
      } catch (error) {
        // Handle permission errors gracefully
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          console.log('Media permissions denied by user - this is expected if user has not granted permissions')
          setMicrophoneError(true)
          setCameraError(true)
        } else {
          console.error('Error checking available devices:', error)
        }
      }
    }
    
    checkDevices()

    // Listen for device changes
    const handleDeviceChange = () => {
      console.log('device change detected')
      const checkDevicesOnChange = async () => {
        try {
          await checkAvailableDevices(
            setMicrophoneError,
            setCameraError,
            setAvailableCameras,
            setAvailableMicrophones,
            setAvailableSpeakers,
            setSelectedCamera,
            setSelectedMicrophone,
            setSelectedSpeaker,
            setGiveBothPermissionsDialogOpen,
          )
        } catch (error) {
          // Handle permission errors gracefully
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
            console.log('Media permissions denied by user - this is expected if user has not granted permissions')
            setMicrophoneError(true)
            setCameraError(true)
          } else {
            console.error('Error checking available devices on change:', error)
          }
        }
      }
      checkDevicesOnChange()
    }

    // Only add event listener if mediaDevices is available
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange)

      return () => {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange)
      }
    }
  }, [])

  // useEffect(() => {
  //   if (
  //     fixedChamberRoomId &&
  //     fixedChamberRoomId !== 'undefined' &&
  //     clinicIdOfFixedChamberRoomId &&
  //     clinicIdOfFixedChamberRoomId !== 'undefined' &&
  //     Boolean(clinicLocationData)
  //   ) {
  //     if (clinicIdOfFixedChamberRoomId !== clinicLocationData?.id) {
  //       setAskResetDialogOpen(true)
  //     }
  //   }
  // }, [])

  async function updateScreenWithAvailableIODevices() {
    // Skip if screenId is not available (e.g., screen not registered yet)
    const screenId = getAumhumScreenId()
    if (!screenId) {
      console.log('Skipping I/O device update: screenId not available yet')
      return
    }

    try {
      const resp = await putDataWithParams(
        '/api/secure/kiosk/screen/config/update-io-devices',
        {
          availableMicrophones: (availableMicrophones || [])?.map((device) => ({
            deviceId: device?.deviceId,
            title: device?.label,
          })),
          availableCameras: (availableCameras || [])?.map((device) => ({
            deviceId: device?.deviceId,
            title: device?.label,
          })),
          availableSpeakers: (availableSpeakers || [])?.map((device) => ({
            deviceId: device?.deviceId,
            title: device?.label,
          })),
        },
        {
          screenId: screenId,
        },
      )
      if (resp?.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          console.log('updated screen with available I/O devices')
        }
      } else {
        console.error('could not update screen with available i/o devices')
      }
    } catch (error) {
      // Handle errors gracefully - don't crash if device update fails
      console.error('Error updating screen with available I/O devices:', error)
    }
  }

  useEffect(() => {
    const time = setTimeout(() => {
      updateScreenWithAvailableIODevices()
    }, 1000)
    return () => {
      clearTimeout(time)
    }
  }, [availableCameras, availableMicrophones, availableSpeakers])

  useEffect(() => {
    // Fetch data from new kiosk APIs
    getAndSetScreenDeviceConfigs()
    getAndSetClinics()
  }, [])

  useEffect(() => {
    getAndHandleNextAction() // Initial call
  }, [])

  useEffect(() => {
    checkAndRefreshTokenIfNeeded() // Initial token check on mount
  }, [])

  useEffect(() => {
    const intervalId = setInterval(() => {
      getAndHandleNextAction()
    }, 60000)
    return () => {
      clearInterval(intervalId)
    }
  }, [getAndHandleNextAction]) // call every 1 minute

  // Lightweight liveness heartbeat (~15s) so the practitioner side detects a switched-off /
  // disconnected screen within a few missed beats — independent of the slower 60s poll above.
  // Backend reports the screen "down" only after ~3 consecutive misses (SCREEN_HEARTBEAT_TIMEOUT).
  useEffect(() => {
    if (!fixedChamberRoomId) {
      return
    }
    const sendHeartbeat = () => {
      // postCPDataWithParams(url, body, queryParams) — the ids MUST go in the query slot (3rd
      // arg) because the backend reads them as @RequestParam. Passing them as the body silently
      // 400s, the heartbeat never lands, and the screen flaps "down" against the 50s timeout.
      postCPDataWithParams(
        `/api/secure/kiosk/chamber-screen/heartbeat`,
        {},
        { chamberRoomId: fixedChamberRoomId, screenId: getAumhumScreenId() },
      ).catch(() => {})
    }
    sendHeartbeat() // immediate beat on mount/registration
    const heartbeatIntervalId = setInterval(sendHeartbeat, 15000)
    return () => {
      clearInterval(heartbeatIntervalId)
    }
  }, [fixedChamberRoomId])

  useEffect(() => {
    const tokenCheckInterval = setInterval(() => {
      checkAndRefreshTokenIfNeeded()
    }, 1800000) // check every 30 minutes
    return () => {
      clearInterval(tokenCheckInterval)
    }
  }, [])

  // wakelock - Only request when page is visible
  useEffect(() => {
    let wakeLock = null

    const requestWakeLock = async () => {
      // Check if page is visible before requesting wake lock
      if (document.hidden || !document.visibilityState || document.visibilityState === 'hidden') {
        console.log('Page is not visible, skipping wake lock request')
        return
      }

      // Check if WakeLock API is available
      if (!('wakeLock' in navigator)) {
        console.log('WakeLock API is not supported')
        return
      }

      try {
        wakeLock = await navigator.wakeLock.request('screen')
        console.log('Screen Wake Lock: is active')

        // Optionally handle the wake lock release
        wakeLock.addEventListener('release', () => {
          console.log('Screen Wake Lock: was released')
        })
      } catch (err) {
        // Silently handle errors (e.g., page not visible, user denied, etc.)
        if (err.name !== 'NotAllowedError') {
          console.error(`${err.name}: ${err.message}`)
        }
      }
    }

    // Request wake lock when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && document.visibilityState === 'visible') {
        requestWakeLock()
      } else if (wakeLock) {
        // Release wake lock when page becomes hidden
        wakeLock.release().catch(() => {})
        wakeLock = null
      }
    }

    // Initial request if page is visible
    if (!document.hidden) {
      requestWakeLock()
    }

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Clean up on component unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (wakeLock) {
        wakeLock.release().then(() => {
          console.log('Screen Wake Lock: was released(cleanup)')
        }).catch(() => {})
      }
    }
  }, [])

  function showWhatsappGroupQROnScreen() {
    if (brandAndClinicsWholeData?.clinicBrand?.webAddressForWhatsappInitialMessage?.qrCodeMediaId) {
      setPopupTimeLeftOnScreen(30)
      setMaxTimeForPopup(30)
      setAdditionalTextAboveImage('Please scan this QR code to message Therapy Support')
      setImageSourceForPopup(
        getImageLink(
          brandAndClinicsWholeData?.clinicBrand?.webAddressForWhatsappInitialMessage?.qrCodeMediaId,
        ),
      )
    }
  }

  function showAUMHUMPaymentQROnScreen() {
    setPopupTimeLeftOnScreen(30)
    setMaxTimeForPopup(30)
    setAdditionalTextAboveImage('Please scan this QR code to pay for your session')
    setImageSourceForPopup(aumhumRazorpayQR)
  }

  function showAnyImageOnScreen(imageId, duration, text) {
    setPopupTimeLeftOnScreen(duration)
    setMaxTimeForPopup(duration)
    setAdditionalTextAboveImage(text)
    setImageSourceForPopup(getImageLink(imageId))
  }

  function showBookingPaymentLinkQRWithPatientDetailsPopup(paymentLinkWithPatientDetails) {
    // setPopupTimeLeftOnScreen(30)
    // setMaxTimeForPopup(30)
    setPopupTimeLeftOnScreen(200)
    setMaxTimeForPopup(200)
    setAdditionalTextAboveImage()
    setPaymentLinkWithQRCodeData(paymentLinkWithPatientDetails)
    // setImageSourceForPopup(paymentLinkWithPatientDetails?.qrCodeMediaId)
  }

  function showPopupBySignalCommand(timeLeft, payload) {
    const popupTimeLeft = timeLeft
    // const popupTimeLeft = 300

    setPopupTimeLeftOnScreen(popupTimeLeft)
    setMaxTimeForPopup(popupTimeLeft)
    setAdditionalTextAboveImage()
  }

  function clearScreen() {
    setPopupTimeLeftOnScreen(0)
    setMaxTimeForPopup(0)
    setImageSourceForPopup(null)
    setPaymentLinkWithQRCodeData(null)
  }

  function unRegisterScreenAndReloadApp() {
    unregisterScreen()
    window.location.reload()
  }

  function unRegisterScreenBasedOnScreenIdAndReloadApp(payload) {
    if (!!getAumhumScreenId() && getAumhumScreenId() !== payload?.screenId) {
      unRegisterScreenAndReloadApp()
    }
  }

  useEffect(() => {
    // Expose both functions to the global window object for calling in DevTools console
    window.showWhatsappGroupQROnScreen = showWhatsappGroupQROnScreen
    window.showAUMHUMPaymentQROnScreen = showAUMHUMPaymentQROnScreen
  }, [setPopupTimeLeftOnScreen, setImageSourceForPopup, brandAndClinicsWholeData])

  // Hide DOCSIDE on this page only
  useEffect(() => {
    setShowDocSide(false)
    return () => {
      setShowDocSide(true) // Re-enable when leaving this page
    }
  }, [setShowDocSide])

  // open image popup if custom event received
  useEffect(() => {
    setFlowId('remote-doctor')
    const handleSignalingEvent = async (event) => {
      console.log('[KIOSK PORTAL] Signal event received:', {
        command: event?.detail?.command,
        fullEvent: event?.detail,
        timestamp: new Date().toISOString()
      })
      
      setPopupType(event?.detail?.command)
      const screenDuration = event?.detail?.payload?.popupScreenDuration
      const payload = event?.detail?.payload

      let command = event?.detail?.command
      let expectedPrefix = 'mentor-command.'
      //remove the prefix if it exists
      if (command.startsWith(expectedPrefix)) {
        console.log('[KIOSK PORTAL] Removing prefix from command:', command)
        command = command.replace(expectedPrefix, '')
        console.log('[KIOSK PORTAL] Command after prefix removal:', command)
      }

      if (command === SIGNALING_COMMANDS.SHOW_PAYMENT_QR_FOR_30_SECONDS) {
        showAUMHUMPaymentQROnScreen()
        return
      }
      if (command === SIGNALING_COMMANDS.SHOW_THERAPY_SUPPORT_WHATSAPP_QR_FOR_30_SECONDS) {
        showWhatsappGroupQROnScreen()
      }
      if (command === SIGNALING_COMMANDS.ASK_NEXT_ACTION_NOW) {
        console.log('[KIOSK PORTAL] ASK_NEXT_ACTION_NOW command received - triggering manualJoinRequestFunction')
        manualJoinRequestFunction()
        return
      }
      if (command === SIGNALING_COMMANDS.JOIN_AGORA_CHANNEL_AND_START_VIDEOINFORM_CHECK_IN) {
        manualJoinRequestFunction()
        return
      }
      if (command === SIGNALING_COMMANDS.SHOW_BOOKING_PAYMENT_LINK_QR_WITH_PATIENT_DETAILS) {
        showBookingPaymentLinkQRWithPatientDetailsPopup(event?.detail?.payload || {})
        return
      }
      if (command === SIGNALING_COMMANDS.CLEAR_ROOM_SCREEN) {
        clearScreen()
        return
      }
      if (command === SIGNALING_COMMANDS.SHOW_BOOKING_PAYMENT_LINK_QR_WITH_PATIENT_DETAILS) {
        showBookingPaymentLinkQRWithPatientDetailsPopup(event?.detail?.payload || {})
        return
      }
      if (command === SIGNALING_COMMANDS.QR_PAYMENT_SUCCESS) {
        showPopupBySignalCommand(screenDuration || 5, payload || {})
        return
      }
      if (command === SIGNALING_COMMANDS.REFRESH_ROOM_SCREEN) {
        toast.info('Refreshing screen in 3 seconds...')
        informChamberRoomChannelLeave(joinedChannelName)
        await sleep(3000)
        window.location.reload()
        return
      }
      if (command === SIGNALING_COMMANDS.ANY_IMAGE) {
        showAnyImageOnScreen(payload?.imageId, payload?.duration, payload?.text)
        return
      }
      if (command === SIGNALING_COMMANDS.ANY_IMAGE) {
        showAnyImageOnScreen(payload?.imageId, payload?.duration, payload?.text)
        return
      }
      if (command === SIGNALING_COMMANDS.DEREGISTER_SCREEN_AND_RELOAD_APP) {
        unRegisterScreenBasedOnScreenIdAndReloadApp(payload)
        return
      }
      if (command === SIGNALING_COMMANDS.BULK_DEREGISTER_SCREEN_AND_RELOAD_APP) {
        unRegisterScreenAndReloadApp()
        return
      }
      console.log('Unknown signaling command:', event?.detail?.command)
    }

    return addWindowSignalingEventListener({
      dispatchEventKey: DISPATCH_EVENT_KEYS.signalingEvent,
      handler: handleSignalingEvent,
    })
  }, [])

  function manualJoinRequestFunction() {
    console.log('[KIOSK PORTAL] manualJoinRequestFunction called - triggering getAndHandleNextAction immediately')
    setAllowedRetryAttempts((prev) => prev - 1)
    getAndHandleNextAction()
    toast.success('Sent Request for Connecting')
  }

  return (
    <div className="vh-100 vw-100 d-flex justify-content-center align-items-center">
      <GlobalAnimations />
      {fixedChamberRoomId ? (
        <PredecidedChamberIdUIComponent
          thisChamberName={thisChamberName}
          thisLocationName={thisLocationName}
          thisBrandName={thisBrandName}
          chamberScreenStateNow={chamberScreenStateNow}
          leaveThisSession={() => leaveThisSession(joinedChannelName)}
          agoraRtcProps={{
            ...agoraRtcProps,
            playbackDeviceId: selectedSpeaker || 'default',
            microphoneDeviceId: selectedMicrophone || 'default',
            cameraDeviceId: selectedCamera || 'default',
          }}
          agoraRtmProps={agoraRtmProps}
          serverGivenNextAction={serverGivenNextAction}
          clinicBrandId={thisChamberRoomData?.clinicBrandId || thisClinicLocationData?.clinicBrandId}
          flowType={FLOW_TYPES.kioskScreen}
          manualJoinAllowed={allowedRetryAttempts > 0}
          manualJoinRequestFunction={manualJoinRequestFunction}
          screenOnSince={screenOnSince}
          cameraError={cameraError}
          microphoneError={microphoneError}
          enableAudio={true}
          enableVideo={true}
          joinCall={() => {}}
          setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
          availableCameras={availableCameras}
          availableMicrophones={availableMicrophones}
          selectedCamera={selectedCamera}
          selectedMicrophone={selectedMicrophone}
          setSelectedCamera={setSelectedCamera}
          setSelectedMicrophone={setSelectedMicrophone}
          setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
          availableSpeakers={availableSpeakers}
          selectedSpeaker={selectedSpeaker}
          setSelectedSpeaker={setSelectedSpeaker}
          checkAndRefreshTokenIfNeeded={checkAndRefreshTokenIfNeeded}
          screenDeviceConfigs={screenDeviceConfigs}
          virtualChamberRooms={virtualChamberRooms}
        />
      ) : (
        <UndecidedChamberIdUIComponent
          virtualChamberRooms={virtualChamberRooms}
          brands={brands}
          wantedClinicLocation={wantedClinicLocation}
          setWantedClinicLocation={setWantedClinicLocation}
          setWantedChamberRoomId={setWantedChamberRoomId}
          wantedChamberRoomId={wantedChamberRoomId}
        />
      )}
      <OpenWhatsappQRComponent
        isVideoOn={chamberScreenStateNow !== CHAMBER_SCREEN_STATES.IDLING}
        mediaId={
          brandAndClinicsWholeData?.clinicBrand?.webAddressForWhatsappInitialMessage?.qrCodeMediaId
        }
      />
      <PopupWithImageComponent
        popupTimeLeftOnScreen={popupTimeLeftOnScreen}
        setPopupTimeLeftOnScreen={setPopupTimeLeftOnScreen}
        imageSourceForPopup={imageSourceForPopup}
        additionalTextAboveImage={additionalTextAboveImage}
        componentToRender={
          paymentLinkWithQRCodeData ? (
            <PaymentLinkQrWithPatientDetails
              paymentLinkWithPatientDetails={paymentLinkWithQRCodeData}
              resetData={() => {
                clearScreen()
              }}
            />
          ) : undefined
        }
        popupProps={{
          paymentLinkWithPatientDetails: paymentLinkWithQRCodeData,
          resetData: () => {
            clearScreen()
          },
        }}
        popupType={poppType}
        maxTimeForPopup={maxTimeForPopup}
      />
      <AskCameraMicPermissionsDialog
        giveBothPermissionsDialogOpen={giveBothPermissionsDialogOpen}
        setGiveBothPermissionsDialogOpen={setGiveBothPermissionsDialogOpen}
        giveMicrophonePermissionDialogOpen={giveMicroPhonePermissionDialogOpen}
        setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
        giveCameraPermissionDialogOpen={giveCamperPermissionDialogOpen}
        setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
        setMicrophoneError={setMicrophoneError}
        setCameraError={setCameraError}
        setAvailableMicrophones={setAvailableMicrophones}
        setSelectedMicrophone={setSelectedMicrophone}
        setAvailableSpeakers={setAvailableSpeakers}
        setSelectedSpeaker={setSelectedSpeaker}
        setAvailableCameras={setAvailableCameras}
        setSelectedCamera={setSelectedCamera}
      />
    </div>
  )
}

const PredecidedChamberIdUIComponent = ({
  thisChamberName,
  thisLocationName,
  thisBrandName,
  chamberScreenStateNow,
  leaveThisSession,
  agoraRtcProps,
  agoraRtmProps,
  serverGivenNextAction,
  clinicBrandId,
  flowType,
  manualJoinAllowed,
  manualJoinRequestFunction,
  screenOnSince,
  // askResetDialogOpen,
  // cameFromClinicLocationData,
  cameraError,
  microphoneError,
  enableAudio,
  enableVideo,
  setGiveCameraPermissionDialogOpen,
  setGiveMicrophonePermissionDialogOpen,
  availableCameras,
  availableMicrophones,
  selectedCamera,
  selectedMicrophone,
  setSelectedCamera,
  setSelectedMicrophone,
  availableSpeakers,
  selectedSpeaker,
  setSelectedSpeaker,
  checkAndRefreshTokenIfNeeded,
  screenDeviceConfigs = [],
  virtualChamberRooms = [],
}) => {
  const navigate = useNavigate()
  // Audio-recovery wiring: the RTC surface registers its runtime commands here; we keep a
  // handle so the "Restore audio" button can re-kick playback + re-acquire the mic on demand.
  const chamberRuntimeCommandsRef = useRef(null)
  const [audioRecoveryReady, setAudioRecoveryReady] = useState(false)
  const [audioRestoreInFlight, setAudioRestoreInFlight] = useState(false)

  const agoraCallbacks = {
    EndCall: () => leaveThisSession(),
    'register-runtime-commands': (commands) => {
      chamberRuntimeCommandsRef.current = commands
      setAudioRecoveryReady(Boolean(commands?.restoreAudio))
    },
  } // contains Agora Engine events as keys and callback functions as their values.

  const handleRestoreChamberAudio = useCallback(async () => {
    if (!chamberRuntimeCommandsRef.current?.restoreAudio) {
      return
    }
    setAudioRestoreInFlight(true)
    try {
      await chamberRuntimeCommandsRef.current.restoreAudio()
    } catch (error) {
      console.warn('[KIOSK PORTAL] Restore audio failed', error)
    }
    setTimeout(() => setAudioRestoreInFlight(false), 1500)
  }, [])

  const { assistantData } = useContext(ClinicDataContext)
  const { profileData } = useContext(ProfileContext)

  const [CAFlowDialogOpen, setCAFlowDialogOpen] = useState(false)
  const [optionsForScreenDeviceConfig, setOptionsForScreenDeviceConfig] = useState([])
  const [optionsForVirtualConsultationDeviceConfig, setOptionsForVirtualConsultationDeviceConfig] =
    useState([])

  const [anchorElForMenu, setAnchorElForMenu] = useState(null)
  const menuOpen = Boolean(anchorElForMenu)
  const handleClickMenuOpener = (event) => {
    setAnchorElForMenu(event.currentTarget)
  }
  const handleCloseMenu = () => {
    setAnchorElForMenu(null)
  }

  const [manageAllowed, setManageAllowed] = useState(false)
  const [managementViewOpen, setManagementViewOpen] = useState(false)
  const [debugDrawerOpen, setDebugDrawerOpen] = useState(false)
  const [sendingDebugReport, setSendingDebugReport] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(isFullscreenActive)

  // Get config - use kiosk-config with fallback to ROLE_CA_CONFIG (consistent with component initialization)
  const kioskConfigStrForDropdown = localStorage.getItem('kiosk-config')
  const roleCaConfigStrForDropdown = localStorage.getItem('ROLE_CA_CONFIG')
  let roleCaConfig = kioskConfigStrForDropdown ? JSON.parse(kioskConfigStrForDropdown) : (roleCaConfigStrForDropdown ? JSON.parse(roleCaConfigStrForDropdown) : {})

  const [anchorElPinPopover, setAnchorElPinPopover] = useState(null)

  const handleClickPinPopover = (event) => {
    setAnchorElPinPopover(event.currentTarget)
  }

  const handleClosePinPopover = () => {
    setAnchorElPinPopover(null)
  }

  const openPinPopover = Boolean(anchorElPinPopover)

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(isFullscreenActive())
    }

    syncFullscreenState()
    document.addEventListener('fullscreenchange', syncFullscreenState)
    document.addEventListener('webkitfullscreenchange', syncFullscreenState)

    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState)
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState)
    }
  }, [])

  const handleToggleFullscreen = useCallback(async () => {
    try {
      await toggleDocumentFullscreen()
    } catch (error) {
      console.warn('[KioskLayout] Failed to toggle fullscreen', error)
    }
  }, [])

  useEffect(() => {
    // Use screen device configs from API 1 (passed as props from parent)
    setOptionsForScreenDeviceConfig(screenDeviceConfigs)
    // Use virtual chamber rooms from API 1 (passed as props from parent)
    setOptionsForVirtualConsultationDeviceConfig(virtualChamberRooms)
  }, [screenDeviceConfigs, virtualChamberRooms])

  const screenDeviceConfig = optionsForScreenDeviceConfig?.find(
    (item) => item?.id === roleCaConfig?.SCREEN_DEVICE_CONFIG_ID,
  )
  const currentScreenId = getAumhumScreenId()
  const tokenExpiryReadable = getAccessTokenExpiryReadable()
  const timeUntilTokenExpiry = getTimeUntilExpiry()
  const showManagementView = manageAllowed && managementViewOpen

  const debugSnapshot = useMemo(() => ({
    generatedAt: new Date().toISOString(),
    runtime: {
      currentPath: window.location.pathname,
      href: window.location.href,
      userAgent: navigator.userAgent,
      appVersion: packageJson.version,
    },
    screen: {
      screenId: currentScreenId || null,
      screenDeviceConfigId: roleCaConfig?.SCREEN_DEVICE_CONFIG_ID || null,
      screenDeviceTitle: screenDeviceConfig?.title || null,
      appInstallationId: getAppInstallationId() || null,
      currentVirtualRoomId: roleCaConfig?.CURRENT_VIRTUAL_ROOM_ID || null,
    },
    location: {
      chamberName: thisChamberName || null,
      clinicLocationName: thisLocationName || null,
      clinicBrandName: thisBrandName || null,
      clinicBrandId: clinicBrandId || null,
    },
    session: {
      chamberScreenState: chamberScreenStateNow,
      joinedChannelName: agoraRtcProps?.channel || null,
      rtcUid: agoraRtcProps?.uid || null,
      hasRtcToken: Boolean(agoraRtcProps?.token),
      hasRtmToken: Boolean(agoraRtmProps?.token),
      rtmUsername: agoraRtmProps?.username || null,
      screenOnSince: screenOnSince?.toISOString?.() || screenOnSince?.format?.() || null,
    },
    token: {
      accessTokenExpiryReadable: tokenExpiryReadable || null,
      timeUntilExpiryHours: timeUntilTokenExpiry?.hours ?? null,
      timeUntilExpiryMinutes: timeUntilTokenExpiry?.minutes ?? null,
      timeUntilExpiryMs: timeUntilTokenExpiry?.totalMs ?? null,
    },
    user: {
      userId: profileData?.userId || profileData?.id || null,
      userName: profileData?.userName || null,
      name: profileData?.name || null,
    },
    devices: {
      cameraError: Boolean(cameraError),
      microphoneError: Boolean(microphoneError),
      selectedCamera: selectedCamera || null,
      selectedMicrophone: selectedMicrophone || null,
      selectedSpeaker: selectedSpeaker || null,
      availableCameras: (availableCameras || []).map((camera) => ({
        deviceId: camera?.deviceId || null,
        label: camera?.label || null,
      })),
      availableMicrophones: (availableMicrophones || []).map((microphone) => ({
        deviceId: microphone?.deviceId || null,
        label: microphone?.label || null,
      })),
      availableSpeakers: (availableSpeakers || []).map((speaker) => ({
        deviceId: speaker?.deviceId || null,
        label: speaker?.label || null,
      })),
    },
    nextAction: serverGivenNextAction || null,
  }), [
    agoraRtcProps,
    agoraRtmProps,
    availableCameras,
    availableMicrophones,
    availableSpeakers,
    cameraError,
    chamberScreenStateNow,
    clinicBrandId,
    currentScreenId,
    microphoneError,
    profileData?.id,
    profileData?.name,
    profileData?.userId,
    profileData?.userName,
    roleCaConfig?.CURRENT_VIRTUAL_ROOM_ID,
    roleCaConfig?.SCREEN_DEVICE_CONFIG_ID,
    screenDeviceConfig?.title,
    screenOnSince,
    selectedCamera,
    selectedMicrophone,
    selectedSpeaker,
    serverGivenNextAction,
    thisBrandName,
    thisChamberName,
    thisLocationName,
    timeUntilTokenExpiry?.hours,
    timeUntilTokenExpiry?.minutes,
    timeUntilTokenExpiry?.totalMs,
    tokenExpiryReadable,
  ])

  const debugSnapshotJson = useMemo(
    () => JSON.stringify(debugSnapshot, null, 2),
    [debugSnapshot],
  )

  const handleSendDebugReport = useCallback(async () => {
    try {
      setSendingDebugReport(true)

      const response = await postCPDataWithoutParams('/api/secure/kiosk/debug/log', {
        type: KIOSK_DEBUG_LOG_TYPE,
        severity: 'info',
        message: `Manual kiosk debug report from ${screenDeviceConfig?.title || 'kiosk screen'}${thisChamberName ? ` • ${thisChamberName}` : ''}`,
        platform: 'kiosk',
        appVersion: packageJson.version,
        clientTimestamp: new Date().toISOString(),
        screenId: currentScreenId || '',
        screenDeviceConfigId: roleCaConfig?.SCREEN_DEVICE_CONFIG_ID || '',
        screenDeviceTitle: screenDeviceConfig?.title || '',
        chamberRoomId: roleCaConfig?.CURRENT_VIRTUAL_ROOM_ID || '',
        userAgent: navigator.userAgent,
        loggedInUserId: profileData?.userId || profileData?.id || '',
        metadata: {
          currentPath: window.location.pathname,
          href: window.location.href,
          chamberScreenState: chamberScreenStateNow,
          joinedChannelName: agoraRtcProps?.channel || '',
          clinicBrandName: thisBrandName || '',
          clinicLocationName: thisLocationName || '',
          chamberName: thisChamberName || '',
          selectedCamera: selectedCamera || '',
          selectedMicrophone: selectedMicrophone || '',
          selectedSpeaker: selectedSpeaker || '',
          accessTokenExpiryReadable: tokenExpiryReadable || '',
          debugSnapshotJson,
        },
      })

      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        toast.success('Debug report sent to developers')
      } else {
        toast.error(response?.data?.message || 'Unable to send debug report')
      }
    } catch (error) {
      console.error('Error sending kiosk debug report:', error)
      toast.error(error?.message || 'Unable to send debug report')
    } finally {
      setSendingDebugReport(false)
    }
  }, [
    agoraRtcProps?.channel,
    chamberScreenStateNow,
    currentScreenId,
    debugSnapshotJson,
    profileData?.id,
    profileData?.userId,
    roleCaConfig?.CURRENT_VIRTUAL_ROOM_ID,
    roleCaConfig?.SCREEN_DEVICE_CONFIG_ID,
    screenDeviceConfig?.title,
    selectedCamera,
    selectedMicrophone,
    selectedSpeaker,
    thisBrandName,
    thisChamberName,
    thisLocationName,
    tokenExpiryReadable,
  ])

  // app reload handling if there is an update
  useEffect(() => {
    const handleAppUpdateAction = async (event) => {
      if (
        !(
          event?.detail?.action === APP_UPDATE_ACTIONS.SUGGEST_UPDATE ||
          event?.detail?.action === APP_UPDATE_ACTIONS.FORCE_UPDATE
        )
      ) {
        window.location.reload()
        return
      }

      if (chamberScreenStateNow !== CHAMBER_SCREEN_STATES.IDLING) {
        return
      }

      let roleCaConfig = localStorage.getItem('ROLE_CA_CONFIG')
      if (roleCaConfig) {
        roleCaConfig = JSON.parse(roleCaConfig)
        // KioskLayout is the remote-doctor chamber screen, so the auto-reload
        // logic always applies here (no flow field needed).
        if (roleCaConfig) {
          // if roleScreenConfig exists - parse it, if doesn't exists, make it with date field today and count 0
          let roleScreenConfig = localStorage.getItem('ROLE_SCREEN_CONFIG')
          if (roleScreenConfig) {
            roleScreenConfig = JSON.parse(roleScreenConfig)
          } else {
            roleScreenConfig = {
              HARD_AUTO_RELOAD_COUNT_FOR_DATE: getCurrentDate(),
              HARD_AUTO_RELOAD_COUNT: 0,
            }
            localStorage.setItem('ROLE_SCREEN_CONFIG', JSON.stringify(roleScreenConfig))
          }
          // if date field is same as today, check count and if less than 3, increase it by 1 and reload
          if (roleScreenConfig?.HARD_AUTO_RELOAD_COUNT_FOR_DATE === getCurrentDate()) {
            if (roleScreenConfig?.HARD_AUTO_RELOAD_COUNT < 3) {
              const updatedRoleScreenConfig = {
                ...roleScreenConfig,
                HARD_AUTO_RELOAD_COUNT: roleScreenConfig.HARD_AUTO_RELOAD_COUNT + 1,
              }
              localStorage.setItem('ROLE_SCREEN_CONFIG', JSON.stringify(updatedRoleScreenConfig))
              toast.info('New Update Received: Screen will restart in 10 seconds')
              await sleep(10000)
              window.location.reload()
            } else {
              // do nothing, have already auto-reloaded more than 3 times today
            }
          } else {
            // if date field is older, update date to today and make count 1 and reload
            const updatedRoleScreenConfig = {
              ...roleScreenConfig,
              HARD_AUTO_RELOAD_COUNT_FOR_DATE: getCurrentDate(),
              HARD_AUTO_RELOAD_COUNT: 1,
            }
            localStorage.setItem('ROLE_SCREEN_CONFIG', JSON.stringify(updatedRoleScreenConfig))
            toast.info('New Update Received: Screen will restart in 10 seconds')
            await sleep(10000)
            window.location.reload()
          }
        }
      }
    }

    window.addEventListener(DISPATCH_EVENT_KEYS.appUpdateActionEvent, handleAppUpdateAction)
    return () => {
      window.removeEventListener(DISPATCH_EVENT_KEYS.appUpdateActionEvent, handleAppUpdateAction)
    }
  }, [chamberScreenStateNow])

  return (
    <>
      {chamberScreenStateNow === CHAMBER_SCREEN_STATES.IDLING && (
        <div style={{ 
          width: '100%', 
          height: '100vh',
          background: 'linear-gradient(135deg, #b8c6f5 0%, #dac3f0 50%, #c5d8f5 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Soft animated background elements */}
          <div style={{
            position: 'absolute',
            top: '10%',
            right: '15%',
            width: '400px',
            height: '400px',
            background: 'rgba(255, 255, 255, 0.4)',
            borderRadius: '50%',
            filter: 'blur(100px)',
            animation: 'float 12s ease-in-out infinite'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '15%',
            left: '10%',
            width: '450px',
            height: '450px',
            background: 'rgba(184, 198, 245, 0.4)',
            borderRadius: '50%',
            filter: 'blur(120px)',
            animation: 'float 15s ease-in-out infinite reverse'
          }} />
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '500px',
            height: '500px',
            background: 'rgba(218, 195, 240, 0.3)',
            borderRadius: '50%',
            filter: 'blur(140px)',
            animation: 'float 18s ease-in-out infinite',
            transform: 'translate(-50%, -50%)'
          }} />
          
          {/* Version badge in corner - for team use only */}
          <div style={{
            position: 'fixed',
            top: '40px',
            right: '8px',
            zIndex: 100,
            fontSize: '11px',
            color: '#9ca3af',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            padding: '4px 10px',
            borderRadius: '8px',
            fontWeight: '500',
            opacity: 0.6
          }}>
            v{packageJson.version}
          </div>

          {/* Small ticker strip at top */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: '32px',
              background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.95) 0%, rgba(90, 61, 136, 0.95) 100%)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
              zIndex: 1000,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* AUMHUM Logo on left */}
            <img
              src={APP_CONSTANTS.APP_LOGO_LINK}
              style={{
                height: '24px',
                width: 'auto',
                borderRadius: '6px',
              }}
              alt="AUMHUM"
            />

            {/* Screen info ticker text */}
            <div
              style={{
                flex: 1,
                textAlign: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '0.3px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '0 16px',
              }}
            >
              {screenDeviceConfig?.title || 'Screen'} installed at {thisLocationName || 'Clinic Location'}
            </div>

            {/* Right side - Manage button and menu */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {manageAllowed && (
                <>
                  <Button
                    size="small"
                    onClick={() => setManagementViewOpen((prev) => !prev)}
                    sx={{
                      color: 'white',
                      minWidth: 0,
                      textTransform: 'none',
                      fontSize: '11px',
                      fontWeight: '700',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.32)',
                      backgroundColor: 'rgba(255, 255, 255, 0.14)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.22)',
                      },
                    }}
                  >
                    {managementViewOpen ? 'Hide panel' : 'Show panel'}
                  </Button>
                  <IconButton
                    id="demo-positioned-button-counter"
                    onClick={handleClickMenuOpener}
                    sx={{
                      color: 'white',
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      },
                    }}
                  >
                    <MoreVertIcon style={{ fontSize: '20px' }} />
                  </IconButton>
                </>
              )}
              {!manageAllowed && (
                <div
                  style={{
                    fontSize: '11px',
                    cursor: 'pointer',
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                  }}
                  onClick={(event) => {
                    handleClickPinPopover(event)
                  }}
                >
                  ⚙️ Manage
                </div>
              )}
            </div>

            <Popover
              id={'pinPopover'}
              open={openPinPopover}
              anchorEl={anchorElPinPopover}
              onClose={handleClosePinPopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  overflow: 'visible',
                },
              }}
            >
              <PinEntryVarOne
                pin={1008}
                onSuccessFunc={() => {
                  handleClosePinPopover()
                  setManageAllowed(true)
                  setManagementViewOpen(true)
                }}
              />
            </Popover>
          </div>

          {/* Manage menu and pharmacy select - moved to ticker area */}
            {screenDeviceConfig?.installationType ===
              SCREEN_DEVICE_CONFIG_INSTALLATION_TYPES.PHARMACY &&
            showManagementView && (
              <div
                style={{
                  position: 'fixed',
                  top: '40px',
                  right: '16px',
                  zIndex: 999,
                  background: 'white',
                  borderRadius: '12px',
                  padding: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(116, 80, 170, 0.2)',
                  maxWidth: 'calc(100vw - 32px)',
                }}
              >
                <Select
                  labelId="virtual-device-configs-list"
                  id="virtual-device-configs-list"
                  value={roleCaConfig?.CURRENT_VIRTUAL_ROOM_ID}
                  onChange={(e) => {
                    const newConfig = {
                      ...roleCaConfig,
                      CURRENT_VIRTUAL_ROOM_ID: e.target.value,
                    }
                    // Kiosk flow - only update kiosk-config (ROLE_CA_CONFIG is for reception tablet flow)
                    localStorage.setItem('kiosk-config', JSON.stringify(newConfig))
                    // ⚠️ TEMPORARY: Also update ROLE_CA_CONFIG for backward compatibility during migration
                    // TODO: Remove this after all existing users are migrated
                    localStorage.setItem('ROLE_CA_CONFIG', JSON.stringify(newConfig))
                    // Determine base path based on current location, not role - portals are separate
                    const currentPath = window.location.pathname
                    const basePath = currentPath.startsWith('/kiosk') ? '/kiosk' : '/ca-home'
                    window.location.href = `${window.location.origin}${basePath}/chamber-screen-home`
                  }}
                  sx={{
                    fontSize: '12px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(116, 80, 170, 0.3)',
                    },
                  }}
                >
                  {optionsForVirtualConsultationDeviceConfig?.map((item) => {
                    if (item?.screenDeviceConfigId !== roleCaConfig?.SCREEN_DEVICE_CONFIG_ID) {
                      return null
                    }
                    return (
                      <MenuItem key={item?.virtualChamberRoomId} value={item?.virtualChamberRoomId}>
                        {item?.displayName || item?.title || item?.virtualChamberRoomId}
                      </MenuItem>
                    )
                  })}
                </Select>
              </div>
            )}
          <div
            className="d-flex flex-column"
            style={{
              margin: 'auto',
              textAlign: 'center',
              justifyContent: 'center',
              height: 'calc(100vh - 32px)', // Full height minus ticker
              overflow: 'hidden', // No scrolling
              position: 'relative',
              zIndex: 5,
              padding: '20px 32px',
              paddingTop: 'calc(20px + 32px)', // Account for ticker height
              boxSizing: 'border-box',
              marginTop: '32px', // Push content below ticker
            }}
          >
            {/* Welcome and Brand Section - Compact */}
            <div style={{ 
              marginBottom: '12px',
              flexShrink: 0,
            }}>
              <div style={{ 
                letterSpacing: '1.5px', 
                fontSize: '18px',
                color: '#4a5568',
                fontWeight: '400',
                marginBottom: '4px',
                lineHeight: '1.2'
              }}>
                Welcome
                <span style={{
                  fontSize: '14px',
                  color: '#9ca3af',
                  fontWeight: '300',
                  letterSpacing: '1px',
                  marginLeft: '8px'
                }}>
                  to
                </span>
              </div>
              <div style={{ 
                textAlign: 'center', 
                fontSize: 'clamp(36px, 5vw, 56px)', 
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px',
                lineHeight: '1.2',
                letterSpacing: '0.5px'
              }}>
                {thisBrandName}
              </div>
              <div style={{ 
                textAlign: 'center',
                fontSize: '14px',
                color: '#5a67d8',
                backgroundColor: 'rgba(255, 255, 255, 0.65)',
                padding: '8px 20px',
                borderRadius: '20px',
                display: 'inline-block',
                backdropFilter: 'blur(15px)',
                border: '1px solid rgba(90, 103, 216, 0.2)',
                fontWeight: '500'
              }}>
                {thisChamberName} • {thisLocationName}
              </div>
            </div>

            {/* Cards Section - Horizontal layout for landscape */}
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ 
                gap: '20px',
                flex: '1 1 auto',
                minHeight: 0,
                flexWrap: 'wrap',
                maxWidth: '100%',
              }}
            >
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.55)',
                backdropFilter: 'blur(20px)',
                padding: '20px 32px',
                borderRadius: '20px',
                border: '1px solid rgba(150, 150, 200, 0.2)',
                boxShadow: '0 4px 24px rgba(150, 150, 200, 0.15)',
                flexShrink: 0,
                minWidth: '280px',
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <DigitalClockComponent
                    stylesForText={{
                      fontSize: 'clamp(40px, 4vw, 52px)',
                      letterSpacing: '3px',
                      fontFamily: 'Orbitron, monospace',
                      fontWeight: '600',
                      color: '#5a67d8',
                      lineHeight: '1.1'
                    }}
                  />
                </div>
                {manualJoinAllowed && (
                  <div
                    style={{ 
                      textAlign: 'center', 
                      cursor: 'pointer',
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      padding: '8px 18px',
                      borderRadius: '12px',
                      color: '#5a67d8',
                      fontWeight: '600',
                      fontSize: '13px',
                      transition: 'all 0.3s ease',
                      marginTop: '10px',
                      border: '1px solid rgba(90, 103, 216, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.85)'
                      e.target.style.transform = 'scale(1.02)'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.7)'
                      e.target.style.transform = 'scale(1)'
                    }}
                    onClick={() => {
                      manualJoinRequestFunction && manualJoinRequestFunction()
                    }}
                  >
                    Reconnect
                  </div>
                )}
                {showManagementView && (
                  <div
                    style={{ 
                      textAlign: 'center', 
                      cursor: 'pointer',
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      padding: '6px 16px',
                      borderRadius: '10px',
                      color: '#718096',
                      fontWeight: '500',
                      fontSize: '11px',
                      marginTop: '8px',
                      transition: 'all 0.3s ease',
                      border: '1px solid rgba(150, 150, 200, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.75)'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.6)'
                    }}
                    onClick={() => {
                      checkAndRefreshTokenIfNeeded()
                    }}
                  >
                    Test Token Refresh
                  </div>
                )}
              </div>
              {showManagementView && (
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.55)',
                  backdropFilter: 'blur(20px)',
                  padding: '16px 20px',
                  borderRadius: '20px',
                  border: '1px solid rgba(150, 150, 200, 0.2)',
                  boxShadow: '0 4px 24px rgba(150, 150, 200, 0.15)',
                  maxWidth: '380px',
                  flexShrink: 0,
                }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#5a67d8', 
                    marginBottom: '10px',
                    fontWeight: '700',
                    letterSpacing: '0.5px'
                  }}>
                    📹 Video & Audio Configuration
                  </div>
                  
                  {/* Camera Selection */}
                  {availableCameras?.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#4a5568', 
                        marginBottom: '4px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span>📷</span>
                        <span>Camera</span>
                      </div>
                      <select
                        value={selectedCamera}
                        onChange={(e) => {
                          setSelectedCameraInLocalStorage(e.target.value)
                          setSelectedCamera(e.target.value)
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(90, 103, 216, 0.3)',
                          backgroundColor: '#fff',
                          fontSize: '12px',
                          color: '#4a5568',
                          fontWeight: '500',
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#5a67d8'
                          e.target.style.boxShadow = '0 0 0 3px rgba(90, 103, 216, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'rgba(90, 103, 216, 0.3)'
                          e.target.style.boxShadow = 'none'
                        }}
                      >
                        {availableCameras?.map((camera) => (
                          <option key={camera?.deviceId} value={camera?.deviceId}>
                            {camera?.label || `Camera ${availableCameras.indexOf(camera) + 1}`}
                          </option>
                        ))}
                      </select>
                      <CameraPreviewCard
                        enabled={showManagementView}
                        selectedCamera={selectedCamera}
                        cameraError={cameraError}
                        onRequestPermissions={() => setGiveCameraPermissionDialogOpen(true)}
                      />
                    </div>
                  )}
                  
                  {/* Microphone Selection */}
                  {availableMicrophones?.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#4a5568', 
                        marginBottom: '4px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span>🎤</span>
                        <span>Microphone</span>
                      </div>
                      <select
                        value={selectedMicrophone}
                        onChange={(e) => {
                          setSelectedMicrophoneInLocalStorage(e.target.value)
                          setSelectedMicrophone(e.target.value)
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(90, 103, 216, 0.3)',
                          backgroundColor: '#fff',
                          fontSize: '12px',
                          color: '#4a5568',
                          fontWeight: '500',
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#5a67d8'
                          e.target.style.boxShadow = '0 0 0 3px rgba(90, 103, 216, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'rgba(90, 103, 216, 0.3)'
                          e.target.style.boxShadow = 'none'
                        }}
                      >
                        {availableMicrophones?.map((mic) => (
                          <option key={mic?.deviceId} value={mic?.deviceId}>
                            {mic?.label || `Microphone ${availableMicrophones.indexOf(mic) + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Speaker Selection */}
                  {availableSpeakers?.length > 0 && (
                    <div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#4a5568', 
                        marginBottom: '4px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span>🔊</span>
                        <span>Speaker</span>
                      </div>
                      <select
                        value={selectedSpeaker}
                        onChange={(e) => {
                          setSelectedSpeakerInLocalStorage(e.target.value)
                          setSelectedSpeaker(e.target.value)
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(90, 103, 216, 0.3)',
                          backgroundColor: '#fff',
                          fontSize: '12px',
                          color: '#4a5568',
                          fontWeight: '500',
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#5a67d8'
                          e.target.style.boxShadow = '0 0 0 3px rgba(90, 103, 216, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'rgba(90, 103, 216, 0.3)'
                          e.target.style.boxShadow = 'none'
                        }}
                      >
                        {availableSpeakers?.map((speaker) => (
                          <option key={speaker?.deviceId} value={speaker?.deviceId}>
                            {speaker?.label || `Speaker ${availableSpeakers.indexOf(speaker) + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  <div style={{
                    marginTop: '12px',
                    fontSize: '11px',
                    color: '#9ca3af',
                    fontStyle: 'italic',
                    textAlign: 'center'
                  }}>
                    Changes apply immediately
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginTop: '12px',
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDebugDrawerOpen(true)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '999px',
                      }}
                    >
                      Open Debug Drawer
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={hardReloadApp}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '999px',
                      }}
                    >
                      Hard Reload App
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setManagementViewOpen(false)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '999px',
                      }}
                    >
                      Close Management View
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div
              className="d-flex w-100 justify-content-between"
              style={{
                paddingLeft: '24px',
                paddingRight: '24px',
                paddingTop: '16px',
                paddingBottom: '16px',
                flexShrink: 0,
                alignItems: 'end',
                marginTop: '80px',
              }}
            >
              <div className="d-flex flex-column" style={{ alignItems: 'flex-start', gap: '10px' }}>
                {showManagementView && (
                  <>
                    <div className="d-flex" style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      padding: '10px 18px',
                      borderRadius: '12px',
                      backdropFilter: 'blur(15px)',
                      border: '1px solid rgba(150, 150, 200, 0.2)'
                    }}>
                      <div
                        style={{
                          minWidth: '220px',
                          textAlign: 'left',
                          color: '#5a67d8',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}
                      >
                        Screen active:{' '}
                      </div>
                      <div style={{ color: '#4a5568', fontWeight: '500', fontSize: '14px' }}>
                        {screenOnSince?.format('DD MMM, h:mm a')}
                    </div>
                    </div>
                    <div className="d-flex" style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      padding: '10px 18px',
                      borderRadius: '12px',
                      backdropFilter: 'blur(15px)',
                      border: '1px solid rgba(150, 150, 200, 0.2)'
                    }}>
                      <div
                        style={{
                          minWidth: '220px',
                          textAlign: 'left',
                          color: '#5a67d8',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}
                      >
                        Connected since:{' '}
                      </div>
                      <div style={{ color: '#4a5568', fontWeight: '500', fontSize: '14px' }}>
                        {screenOnSince?.format('DD MMM, h:mm a')}
                    </div>
                    </div>
                  </>
                )}
                <TokenExpiryTimeForChamberRoom />
              </div>
              {showManagementView && (
                <div className="d-flex flex-column" style={{ 
                  alignItems: 'end',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  padding: '14px 22px',
                  borderRadius: '16px',
                  backdropFilter: 'blur(15px)',
                  border: '1px solid rgba(150, 150, 200, 0.2)',
                  gap: '8px'
                }}>
                  <div style={{ 
                    color: '#4a5568',
                    fontWeight: '600',
                    fontSize: '15px'
                  }}>
                    {profileData?.name}
                  </div>
                  <div
                    style={{
                      cursor: 'pointer',
                      color: '#fff',
                      backgroundColor: '#e53e3e',
                      padding: '7px 18px',
                      borderRadius: '10px',
                      fontWeight: '600',
                      fontSize: '13px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#c53030'
                      e.target.style.transform = 'scale(1.03)'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#e53e3e'
                      e.target.style.transform = 'scale(1)'
                    }}
                    onClick={() => {
                      logoutHandler()
                      navigate('/')
                    }}
                  >
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {chamberScreenStateNow === CHAMBER_SCREEN_STATES.SESSION_RUNNING && (
        <>
          {/* Session chrome: two floating clusters (no full-width strip) so video stays visible between them */}
          <div
            style={{
              position: 'fixed',
              top: '14px',
              left: '18px',
              zIndex: 1101,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              maxWidth: 'min(560px, calc(100vw - 200px))',
              padding: '10px 14px',
              minHeight: '52px',
              boxSizing: 'border-box',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.82) 0%, rgba(245, 243, 255, 0.74) 100%)',
              backdropFilter: 'blur(18px) saturate(140%)',
              WebkitBackdropFilter: 'blur(18px) saturate(140%)',
              borderRadius: '20px',
              border: '1px solid rgba(216, 180, 254, 0.72)',
              boxShadow:
                '0 22px 52px rgba(91, 33, 182, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
            }}
          >
            <img
              src={APP_CONSTANTS.APP_LOGO_LINK}
              style={{
                height: '30px',
                width: 'auto',
                borderRadius: '9px',
                flexShrink: 0,
              }}
              alt="AUMHUM"
            />
            <div
              style={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                gap: '1px',
                textAlign: 'left',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  color: '#4c1d95',
                  fontSize: '13px',
                  fontWeight: 700,
                  letterSpacing: '0.01em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {screenDeviceConfig?.title || thisChamberName || 'Chamber Screen'}
              </div>
              <div
                style={{
                  color: 'rgba(76, 29, 149, 0.72)',
                  fontSize: '11px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {thisLocationName || 'Clinic Location'}
              </div>
            </div>
          </div>

          <div
            style={{
              position: 'fixed',
              top: '14px',
              right: '18px',
              zIndex: 1101,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 10px',
              minHeight: '52px',
              boxSizing: 'border-box',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.82) 0%, rgba(245, 243, 255, 0.74) 100%)',
              backdropFilter: 'blur(18px) saturate(140%)',
              WebkitBackdropFilter: 'blur(18px) saturate(140%)',
              borderRadius: '20px',
              border: '1px solid rgba(216, 180, 254, 0.72)',
              boxShadow:
                '0 22px 52px rgba(91, 33, 182, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
            }}
          >
            <IconButton
              onClick={handleToggleFullscreen}
              sx={{
                width: 36,
                height: 36,
                color: '#5b21b6',
                backgroundColor: 'rgba(255, 255, 255, 0.76)',
                border: '1px solid rgba(216, 180, 254, 0.72)',
                boxShadow: '0 12px 26px rgba(139, 92, 246, 0.14)',
                '&:hover': {
                  backgroundColor: '#ffffff',
                },
              }}
            >
              {isFullscreen ? (
                <FullscreenExitRoundedIcon sx={{ fontSize: 20 }} />
              ) : (
                <FullscreenRoundedIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
            {manageAllowed && (
              <>
                <Button
                  size="small"
                  onClick={() => setManagementViewOpen((prev) => !prev)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '14px',
                    minWidth: 0,
                    padding: '8px 12px',
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#5b21b6',
                    backgroundColor: 'rgba(255, 255, 255, 0.76)',
                    border: '1px solid rgba(216, 180, 254, 0.72)',
                    boxShadow: '0 12px 26px rgba(139, 92, 246, 0.14)',
                    '&:hover': {
                      backgroundColor: '#ffffff',
                    },
                  }}
                >
                  {managementViewOpen ? 'Hide panel' : 'Show panel'}
                </Button>
                <IconButton
                  id="demo-positioned-button-counter"
                  onClick={handleClickMenuOpener}
                  sx={{
                    width: 36,
                    height: 36,
                    color: '#5b21b6',
                    backgroundColor: 'rgba(255, 255, 255, 0.76)',
                    border: '1px solid rgba(216, 180, 254, 0.72)',
                    boxShadow: '0 12px 26px rgba(139, 92, 246, 0.14)',
                    '&:hover': {
                      backgroundColor: '#ffffff',
                    },
                  }}
                >
                  <MoreVertIcon style={{ fontSize: '20px' }} />
                </IconButton>
              </>
            )}
            {!manageAllowed && (
              <div
                style={{
                  fontSize: '11px',
                  cursor: 'pointer',
                  color: '#5b21b6',
                  backgroundColor: 'rgba(255, 255, 255, 0.76)',
                  padding: '8px 12px',
                  borderRadius: '14px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(216, 180, 254, 0.72)',
                  boxShadow: '0 12px 26px rgba(139, 92, 246, 0.14)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#ffffff'
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.76)'
                }}
                onClick={(event) => {
                  handleClickPinPopover(event)
                }}
              >
                Manage
              </div>
            )}
          </div>

          <Popover
            id={'pinPopover-session'}
            open={openPinPopover}
            anchorEl={anchorElPinPopover}
            onClose={handleClosePinPopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                backgroundColor: 'transparent',
                boxShadow: 'none',
                overflow: 'visible',
              },
            }}
          >
            <PinEntryVarOne
              pin={1008}
              onSuccessFunc={() => {
                handleClosePinPopover()
                setManageAllowed(true)
                setManagementViewOpen(true)
              }}
            />
          </Popover>
          {showManagementView && (
            <div
              style={{
                position: 'fixed',
                top: '82px',
                right: '18px',
                zIndex: 1101,
                width: 'min(360px, calc(100vw - 36px))',
                background:
                  'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(245, 243, 255, 0.85) 100%)',
                backdropFilter: 'blur(18px) saturate(140%)',
                WebkitBackdropFilter: 'blur(18px) saturate(140%)',
                borderRadius: '22px',
                border: '1px solid rgba(216, 180, 254, 0.72)',
                boxShadow:
                  '0 22px 52px rgba(91, 33, 182, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                padding: '16px',
              }}
            >
              <div
                style={{
                  color: '#4c1d95',
                  fontSize: '14px',
                  fontWeight: '700',
                  marginBottom: '6px',
                }}
              >
                Management Panel
              </div>
              <div
                style={{
                  color: 'rgba(76, 29, 149, 0.72)',
                  fontSize: '12px',
                  marginBottom: '14px',
                  lineHeight: 1.5,
                }}
              >
                Screen: {screenDeviceConfig?.title || 'Configured screen'}
                {thisChamberName ? ` • ${thisChamberName}` : ''}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setDebugDrawerOpen(true)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                  }}
                >
                  Open Debug Drawer
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={hardReloadApp}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                  }}
                >
                  Hard Reload App
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setManagementViewOpen(false)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                  }}
                >
                  Close Management View
                </Button>
              </div>
            </div>
          )}
          <div
            className="kiosk-chamber-full-bleed-video"
            style={{ position: 'fixed', inset: 0, width: '100%', height: '100vh', zIndex: 1000 }}
          >
            <ModernChamberVideoInterface
              agoraRtcProps={agoraRtcProps}
              agoraRtmProps={agoraRtmProps}
              agoraCallbacks={agoraCallbacks}
              serverGivenNextAction={serverGivenNextAction}
              localParticipantIdentity={readLocalParticipantIdentity(
                serverGivenNextAction?.chamberScreenAction || serverGivenNextAction,
                createChamberScreenLocalParticipantIdentity(
                  serverGivenNextAction?.chamberScreenAction || serverGivenNextAction,
                  getAumhumScreenId() ||
                    roleCaConfig?.SCREEN_DEVICE_CONFIG_ID ||
                    roleCaConfig?.CURRENT_VIRTUAL_ROOM_ID,
                ),
              )}
              chamberName={thisChamberName}
              clinicBrandId={clinicBrandId}
              flowType={flowType}
              sessionRoutingDecision={
                serverGivenNextAction?.sessionRoutingDecision ||
                serverGivenNextAction?.chamberScreenAction?.sessionRoutingDecision ||
                null
              }
            />
            {audioRecoveryReady && (
              <Box sx={{ position: 'fixed', left: 16, bottom: 16, zIndex: 2000 }}>
                <Button
                  variant="contained"
                  onClick={handleRestoreChamberAudio}
                  disabled={audioRestoreInFlight}
                  sx={{
                    textTransform: 'none',
                    fontSize: '15px',
                    fontWeight: 600,
                    borderRadius: '999px',
                    px: 2.5,
                    py: 1.1,
                    color: '#fff',
                    backgroundColor: 'rgba(15, 15, 18, 0.72)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.35)',
                    '&:hover': { backgroundColor: 'rgba(15, 15, 18, 0.85)' },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(15,15,18,0.55)' },
                  }}
                >
                  {audioRestoreInFlight ? 'Restoring audio…' : "Can't hear the doctor? Restore audio"}
                </Button>
              </Box>
            )}
          </div>
        </>
      )}
      <KioskDebugDrawer
        open={debugDrawerOpen}
        onClose={() => setDebugDrawerOpen(false)}
        debugSnapshot={debugSnapshot}
        sendingDebugReport={sendingDebugReport}
        onSendDebugReport={handleSendDebugReport}
        onHardReload={hardReloadApp}
      />
      <CADeviceUseFixingComponent
        CAFlowDialogOpen={CAFlowDialogOpen}
        setCAFlowDialogOpen={setCAFlowDialogOpen}
      />
      <CSMenuComponent
        anchorElForMenu={anchorElForMenu}
        menuOpen={menuOpen}
        handleCloseMenu={handleCloseMenu}
        openDeviceUseSettings={() => setCAFlowDialogOpen(true)}
        openDebugDrawer={() => setDebugDrawerOpen(true)}
        hardReloadApp={hardReloadApp}
      />
    </>
  )
}

const UndecidedChamberIdUIComponent = ({
  virtualChamberRooms,
  brands,
  wantedClinicLocation,
  setWantedClinicLocation,
  setWantedChamberRoomId,
  wantedChamberRoomId,
}) => {
  const navigate = useNavigate()
  const [CAFlowDialogOpen, setCAFlowDialogOpen] = useState(false)

  let roleCaConfig = localStorage.getItem('ROLE_CA_CONFIG')
  if (roleCaConfig) {
    roleCaConfig = JSON.parse(roleCaConfig)
  }

  return (
    <div style={{ maxWidth: '400px' }}>
      <div className="w-100 d-flex" style={{ justifyContent: 'right', paddingRight: '16px' }}>
        <SettingsIcon onClick={() => setCAFlowDialogOpen(true)} style={{ cursor: 'pointer' }} />
        <div style={{ position: 'absolute', top: '4px', right: '8px' }}>
          <CADeviceUseFixingComponent
            CAFlowDialogOpen={CAFlowDialogOpen}
            setCAFlowDialogOpen={setCAFlowDialogOpen}
          />
        </div>
      </div>
      <div>
        Screen not configured to be used as Always On Screen. Please click settings icon above and
        configure.
      </div>
    </div>
  )
}

function UpcomingSessionsComponent() {
  return (
    <div
      style={{
        width: '100%',
        minHeight: '120px',
        color: '#4a5568',
        textAlign: 'center'
      }}
    >
      <div style={{ 
        fontSize: '17px',
        fontWeight: '600',
        marginBottom: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        color: '#5a67d8'
      }}>
        <TodayIcon style={{ fontSize: '22px' }} />
        <span>Today&apos;s Schedule</span>
      </div>
      <div style={{
        fontSize: '15px',
        color: '#718096',
        fontWeight: '500'
      }}>
        No sessions scheduled
      </div>
    </div>
  )
}

const CSMenuComponent = ({
  anchorElForMenu,
  menuOpen,
  handleCloseMenu,
  openDeviceUseSettings,
  openDebugDrawer,
  hardReloadApp,
}) => {
  const navigate = useNavigate()
  return (
    <Menu
      id="demo-positioned-menu"
      aria-labelledby="demo-positioned-button"
      anchorEl={anchorElForMenu}
      open={menuOpen}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(150, 150, 200, 0.2)',
          boxShadow: '0 8px 32px rgba(150, 150, 200, 0.25)',
          borderRadius: '16px',
          color: '#4a5568'
        }
      }}
    >
      <MenuItem
        onClick={() => {
          // Determine if we're in kiosk portal and navigate accordingly
          const currentPath = window.location.pathname
          if (currentPath.startsWith('/kiosk')) {
            navigate('/kiosk/select-flow')
          } else {
            openDeviceUseSettings()
          }
          handleCloseMenu()
        }}
        sx={{
          '&:hover': {
            backgroundColor: 'rgba(184, 198, 245, 0.2)',
          },
          padding: '12px 20px',
          fontSize: '15px',
          fontWeight: '500',
          color: '#4a5568'
        }}
      >
        <SettingsIcon style={{ marginRight: '12px', fontSize: '20px', color: '#5a67d8' }} />
        {window.location.pathname.startsWith('/kiosk') ? 'Change Kiosk' : 'Device Settings'}
      </MenuItem>
      <MenuItem
        onClick={() => {
          openDebugDrawer()
          handleCloseMenu()
        }}
        sx={{
          '&:hover': {
            backgroundColor: 'rgba(184, 198, 245, 0.2)',
          },
          padding: '12px 20px',
          color: '#4a5568'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '20px' }}>🐛</span>
          <div>
            <div style={{ fontWeight: '600', fontSize: '15px', color: '#4a5568' }}>Debug Drawer</div>
            <div style={{ fontSize: '12px', color: '#718096' }}>Open full screen diagnostics</div>
          </div>
        </div>
      </MenuItem>
      <MenuItem
        onClick={() => {
          hardReloadApp()
          handleCloseMenu()
        }}
        sx={{
          '&:hover': {
            backgroundColor: 'rgba(184, 198, 245, 0.2)',
          },
          padding: '12px 20px',
          color: '#4a5568'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '20px' }}>🔄</span>
          <div>
            <div style={{ fontWeight: '600', fontSize: '15px', color: '#4a5568' }}>Hard Reload App</div>
            <div style={{ fontSize: '12px', color: '#718096' }}>Reload without trusting current state</div>
          </div>
        </div>
      </MenuItem>
      <MenuItem
        onClick={() => {
          navigate('/help-center')
          handleCloseMenu()
        }}
        sx={{
          '&:hover': {
            backgroundColor: 'rgba(184, 198, 245, 0.2)',
          },
          padding: '12px 20px',
          color: '#4a5568'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '20px' }}>💬</span>
        <div>
            <div style={{ fontWeight: '600', fontSize: '15px', color: '#4a5568' }}>Need Help?</div>
            <div style={{ fontSize: '12px', color: '#718096' }}>Contact support</div>
          </div>
        </div>
      </MenuItem>
      <MenuItem
        onClick={() => {
          logoutHandler()
          navigate('/')
          handleCloseMenu()
        }}
        sx={{
          '&:hover': {
            backgroundColor: 'rgba(229, 62, 62, 0.1)',
          },
          padding: '12px 20px',
          fontSize: '15px',
          fontWeight: '600',
          color: '#e53e3e',
          borderTop: '1px solid rgba(150, 150, 200, 0.2)',
          marginTop: '8px'
        }}
      >
        <span style={{ fontSize: '20px', marginRight: '12px' }}>🚪</span>
        Logout
      </MenuItem>
    </Menu>
  )
}

const CameraPreviewCard = ({ enabled, selectedCamera, cameraError, onRequestPermissions }) => {
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const [previewError, setPreviewError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const stopStream = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
        streamRef.current = null
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }

    let isCancelled = false

    if (!enabled || !selectedCamera || !navigator.mediaDevices?.getUserMedia) {
      stopStream()
      setLoading(false)
      setPreviewError('')
      return () => {
        stopStream()
      }
    }

    const startPreview = async () => {
      try {
        setLoading(true)
        setPreviewError('')
        stopStream()

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: {
              exact: selectedCamera,
            },
          },
          audio: false,
        })

        if (isCancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
      } catch (error) {
        console.error('[KioskLayout] camera preview failed', error)
        if (!isCancelled) {
          setPreviewError(error?.message || 'Unable to start camera preview')
        }
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }

    startPreview()

    return () => {
      isCancelled = true
      stopStream()
    }
  }, [enabled, selectedCamera])

  return (
    <div
      style={{
        marginTop: '10px',
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'rgba(76, 29, 149, 0.06)',
        border: '1px solid rgba(90, 103, 216, 0.18)',
      }}
    >
      <div
        style={{
          padding: '8px 10px',
          fontSize: '11px',
          fontWeight: '700',
          color: '#5a67d8',
          borderBottom: '1px solid rgba(90, 103, 216, 0.12)',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
        }}
      >
        Live Camera Preview
      </div>
      <div
        style={{
          position: 'relative',
          aspectRatio: '16 / 9',
          backgroundColor: '#0f172a',
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: loading || previewError || cameraError ? 'none' : 'block',
          }}
        />
        {(loading || previewError || cameraError || !selectedCamera) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              color: '#e2e8f0',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '24px' }}>{loading ? '⏳' : '📷'}</div>
            <div style={{ fontSize: '12px', fontWeight: '600' }}>
              {loading
                ? 'Starting preview...'
                : previewError || cameraError
                  ? 'Camera preview unavailable'
                  : 'Select a camera to preview'}
            </div>
            {(previewError || cameraError) && (
              <Button
                size="small"
                variant="contained"
                onClick={onRequestPermissions}
                sx={{
                  textTransform: 'none',
                  borderRadius: '999px',
                }}
              >
                Review Camera Permissions
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const KioskDebugDrawer = ({
  open,
  onClose,
  debugSnapshot,
  sendingDebugReport,
  onSendDebugReport,
  onHardReload,
}) => {
  const overviewRows = [
    ['Screen title', debugSnapshot?.screen?.screenDeviceTitle || 'Unavailable'],
    ['Screen ID', debugSnapshot?.screen?.screenId || 'Unavailable'],
    ['Screen config ID', debugSnapshot?.screen?.screenDeviceConfigId || 'Unavailable'],
    ['Installation ID', debugSnapshot?.screen?.appInstallationId || 'Unavailable'],
    ['Chamber state', debugSnapshot?.session?.chamberScreenState || 'Unavailable'],
    ['Joined channel', debugSnapshot?.session?.joinedChannelName || 'Unavailable'],
    ['RTC UID', debugSnapshot?.session?.rtcUid || 'Unavailable'],
    ['Brand', debugSnapshot?.location?.clinicBrandName || 'Unavailable'],
    ['Location', debugSnapshot?.location?.clinicLocationName || 'Unavailable'],
    ['Chamber', debugSnapshot?.location?.chamberName || 'Unavailable'],
    ['Selected camera', debugSnapshot?.devices?.selectedCamera || 'Unavailable'],
    ['Selected microphone', debugSnapshot?.devices?.selectedMicrophone || 'Unavailable'],
    ['Selected speaker', debugSnapshot?.devices?.selectedSpeaker || 'Unavailable'],
    ['Access token expiry', debugSnapshot?.token?.accessTokenExpiryReadable || 'Unavailable'],
    ['App version', debugSnapshot?.runtime?.appVersion || 'Unavailable'],
  ]

  const renderSection = (title, rows) => (
    <div style={{ marginBottom: '20px' }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          color: '#4c1d95',
          marginBottom: '8px',
          letterSpacing: '0.01em',
        }}
      >
        {title}
      </Typography>
      <div
        style={{
          borderRadius: '14px',
          border: '1px solid rgba(196, 181, 253, 0.55)',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
        }}
      >
        {rows.map(([label, value], index) => (
          <div
            key={`${title}-${label}`}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '10px 12px',
              borderBottom:
                index === rows.length - 1 ? 'none' : '1px solid rgba(237, 233, 254, 0.9)',
              alignItems: 'flex-start',
            }}
          >
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#6d28d9' }}>{label}</div>
            <div
              style={{
                fontSize: '12px',
                color: '#334155',
                textAlign: 'right',
                wordBreak: 'break-word',
              }}
            >
              {String(value)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 'min(520px, 100vw)',
          background:
            'linear-gradient(180deg, rgba(250, 245, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
        },
      }}
    >
      <Box sx={{ padding: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          <div>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#4c1d95' }}>
              Kiosk Debug Drawer
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', marginTop: '4px' }}>
              Full runtime snapshot for this screen, ready to inspect or send to developers.
            </Typography>
          </div>
          <Button onClick={onClose} size="small" sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginBottom: '18px',
          }}
        >
          <Button
            variant="contained"
            onClick={onSendDebugReport}
            disabled={sendingDebugReport}
            sx={{ textTransform: 'none', borderRadius: '999px' }}
          >
            {sendingDebugReport ? 'Sending…' : 'Send to Developers'}
          </Button>
          <Button
            variant="outlined"
            onClick={onHardReload}
            sx={{ textTransform: 'none', borderRadius: '999px' }}
          >
            Hard Reload App
          </Button>
        </div>

        <Box sx={{ overflowY: 'auto', paddingRight: '4px' }}>
          {renderSection('Overview', overviewRows)}
          {renderSection('Session Tokens', [
            ['Access expires in (hours)', debugSnapshot?.token?.timeUntilExpiryHours ?? 'Unavailable'],
            ['Access expires in (minutes)', debugSnapshot?.token?.timeUntilExpiryMinutes ?? 'Unavailable'],
            ['Has RTC token', debugSnapshot?.session?.hasRtcToken ? 'Yes' : 'No'],
            ['Has RTM token', debugSnapshot?.session?.hasRtmToken ? 'Yes' : 'No'],
            ['RTM username', debugSnapshot?.session?.rtmUsername || 'Unavailable'],
          ])}
          {renderSection('Detected Devices', [
            ['Available cameras', debugSnapshot?.devices?.availableCameras?.length ?? 0],
            ['Available microphones', debugSnapshot?.devices?.availableMicrophones?.length ?? 0],
            ['Available speakers', debugSnapshot?.devices?.availableSpeakers?.length ?? 0],
            ['Camera error', debugSnapshot?.devices?.cameraError ? 'Yes' : 'No'],
            ['Microphone error', debugSnapshot?.devices?.microphoneError ? 'Yes' : 'No'],
          ])}
          <div style={{ marginBottom: '12px' }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: '#4c1d95',
                marginBottom: '8px',
                letterSpacing: '0.01em',
              }}
            >
              Raw Snapshot
            </Typography>
            <div
              style={{
                borderRadius: '14px',
                border: '1px solid rgba(196, 181, 253, 0.55)',
                backgroundColor: '#0f172a',
                color: '#e2e8f0',
                padding: '14px',
                overflowX: 'auto',
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontSize: '11px',
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(debugSnapshot, null, 2)}
              </pre>
            </div>
          </div>
        </Box>
      </Box>
    </Drawer>
  )
}

const getCurrentDate = () => {
  const today = new Date()
  const day = String(today.getDate()).padStart(2, '0')
  const month = String(today.getMonth() + 1).padStart(2, '0') // Months are zero-indexed
  const year = today.getFullYear()
  return `${day}-${month}-${year}`
}

const isFullscreenActive = () => {
  if (typeof document === 'undefined') {
    return false
  }

  return Boolean(document.fullscreenElement || document.webkitFullscreenElement)
}

async function toggleDocumentFullscreen() {
  if (typeof document === 'undefined') {
    return
  }

  const doc = document
  const docElement = doc.documentElement

  if (!isFullscreenActive()) {
    const requestFullscreen =
      docElement.requestFullscreen || docElement.webkitRequestFullscreen

    if (typeof requestFullscreen === 'function') {
      await requestFullscreen.call(docElement)
    }
    return
  }

  const exitFullscreen = doc.exitFullscreen || doc.webkitExitFullscreen

  if (typeof exitFullscreen === 'function') {
    await exitFullscreen.call(doc)
  }
}

const OpenWhatsappQRComponent = ({ isVideoOn = false, mediaId }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  // Don't show QR code on mobile devices
  if (isMobile) {
    return <></>
  }
  
  if (mediaId) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '96px',
          right: '24px',
          zIndex: '1100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            position: 'relative',
            padding: '10px',
            borderRadius: '24px',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.82) 0%, rgba(245, 243, 255, 0.74) 100%)',
            border: '1px solid rgba(216, 180, 254, 0.72)',
            boxShadow:
              '0 22px 52px rgba(91, 33, 182, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(18px) saturate(140%)',
            WebkitBackdropFilter: 'blur(18px) saturate(140%)',
          }}
        >
          <ImageCompForQR alt="qr_image" src={getImageLink(mediaId)} isVideoOn={isVideoOn} />
        </div>
      </div>
    )
  } else {
    return <></>
  }
}

const PopupWithImageComponent = ({
  popupTimeLeftOnScreen,
  setPopupTimeLeftOnScreen,
  imageSourceForPopup,
  maxTimeForPopup,
  additionalTextAboveImage = '',
  componentToRender,
  popupType,
  popupProps = {},
}) => {
  useEffect(() => {
    if (popupTimeLeftOnScreen > 0) {
      // Set interval to decrement the time every second
      const timer = setInterval(() => {
        setPopupTimeLeftOnScreen((prevTime) => prevTime - 1)
      }, 1000)

      // Clean up the interval when the component is unmounted or time reaches 0
      return () => clearInterval(timer)
    }
  }, [popupTimeLeftOnScreen, setPopupTimeLeftOnScreen])

  if (popupTimeLeftOnScreen <= 0) {
    return null
  }

  // function toRender() {
  //   if (componentToRender) {
  //     return componentToRender
  //   }
  //   if (imageSourceForPopup) {
  //     return (
  //       <img
  //         style={{
  //           width: '90%',
  //           height: '90%',
  //           objectFit: 'contain', // Maintain aspect ratio
  //         }}
  //         alt="scannableqrorotherimage"
  //         src={imageSourceForPopup}
  //       />
  //     )
  //   }
  //   return <div>ERR: Unable to locate source image. Please contact AUMHUM support.</div>
  // }

  return (
    <Dialog
      open={true}
      onClose={() => setPopupTimeLeftOnScreen(0)} // Close when user clicks close
      PaperProps={{
        sx: {
          padding: '16px',
          borderRadius: '16px',
          width: '90vw',
          height: '90vh',
          maxWidth: '1200px',
          maxHeight: '800px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
    >
      <>
        {additionalTextAboveImage && (
          <div style={{ fontSize: '18px', fontWeight: '500', maxWidth: '600px' }}>
            {additionalTextAboveImage}
          </div>
        )}
        {/* {toRender()} */}
        <PopupQRInnerComponent
          type={popupType}
          imageSourceForPopup={imageSourceForPopup}
          {...popupProps}
        />
        <div style={{ width: '100%', marginTop: '16px' }}>
          <LinearProgress
            variant="determinate"
            value={(popupTimeLeftOnScreen / maxTimeForPopup) * 100}
          />
        </div>
      </>
    </Dialog>
  )
}

const ImageCompForQR = styled.img`
  width: ${(props) => (props?.isVideoOn ? '40px' : '80px')};
  height: ${(props) => (props?.isVideoOn ? '40px' : '80px')};
  display: block;
  border-radius: 16px;
  @media (min-width: 1400px) {
    width: ${(props) => (props?.isVideoOn ? '80px' : '160px')};
    height: ${(props) => (props?.isVideoOn ? '80px' : '160px')};
  }
`
